"use client";

import React, { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import ResponsiveDialog from "@/src/shared/components/ResponsiveDialog";
import { cn } from "@/src/lib/utils";
import { usePublicListingReviewsQuery } from "../api/queries";
import type { IReviewMention } from "../types";
import ReviewItemSkeleton from "./skeletons/ReviewItemSkeleton";
import PublicReviewCard from "./PublicReviewCard";

interface IAllReviewsDialogProps {
  listingId: number;
  isOpen: boolean;
  onClose: () => void;
  title: string;
  mentions: IReviewMention[];
  /** The chip the list is narrowed to; null for every review. */
  mention: string | null;
  onMentionChange: (mention: string | null) => void;
}

/** Every review, page by page, narrowed by the "guests mention" chips. */
const AllReviewsDialog: React.FC<IAllReviewsDialogProps> = ({
  listingId,
  isOpen,
  onClose,
  title,
  mentions,
  mention,
  onMentionChange,
}) => {
  const { data, isPending, hasNextPage, isFetchingNextPage, fetchNextPage } =
    usePublicListingReviewsQuery(listingId, mention);
  const reviews = data?.pages.flatMap((page) => page.data ?? []) ?? [];
  const selected = mentions.find((chip) => chip.slug === mention) ?? null;

  // Load the next page as the end of the list scrolls into view.
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = sentinel.current;
    if (!isOpen || !node || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isOpen, hasNextPage, isFetchingNextPage, fetchNextPage, reviews.length]);

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      // Switching topics changes how many reviews there are: keep the sheet still.
      fixedHeight
      wide
    >
      <div className="px-6 pb-8 pt-2">
        {mentions.length > 0 && (
          <div className="-mx-6 mb-6 flex gap-2 overflow-x-auto px-6 pb-1 scrollbar-hide">
            {[
              { slug: null, label: "All", icon_url: null, count: null },
              ...mentions,
            ].map((chip) => (
              <button
                key={chip.slug ?? "all"}
                type="button"
                onClick={() => onMentionChange(chip.slug)}
                aria-pressed={mention === chip.slug}
                // Selected: a dark outline and bold label, as the app's tab and Airbnb.
                className={cn(
                  "flex shrink-0 items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm text-ink transition-colors",
                  mention === chip.slug
                    ? "border-2 border-[#222222] font-bold"
                    : "border border-[#EBEBEB] font-medium hover:border-[#222222]",
                )}
              >
                {chip.icon_url && (
                  <img src={chip.icon_url} alt="" className="h-5 w-5" />
                )}
                {chip.label}
                {chip.count !== null && (
                  <span className="font-normal text-ink-secondary">
                    {chip.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        {selected && (
          // The chosen topic, as the app's `_SelectedMentionHeader`.
          <div className="mb-6 flex flex-col items-center border-b border-[#EBEBEB] pb-6 text-center">
            {selected.icon_url && (
              <img
                src={selected.icon_url}
                alt=""
                className="mb-3 h-12 w-12 object-contain"
              />
            )}
            <h3 className="text-22 font-bold text-ink-black">
              {selected.label}
            </h3>
            <p className="mt-1.5 text-13 text-ink-secondary">
              Based on {selected.count}{" "}
              {selected.count === 1 ? "review" : "reviews"}
            </p>
          </div>
        )}
        {isPending ? (
          <div className="flex flex-col gap-8">
            {Array.from({ length: 3 }, (_, i) => (
              <ReviewItemSkeleton key={i} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col divide-y divide-[#EBEBEB]">
            {reviews.map((review) => (
              <div key={review.id} className="py-6 first:pt-0">
                <PublicReviewCard review={review} />
              </div>
            ))}
          </div>
        )}
        <div ref={sentinel} />
        {isFetchingNextPage && (
          <div className="flex justify-center py-4">
            <Loader2 className="animate-spin text-ink-muted" />
          </div>
        )}
      </div>
    </ResponsiveDialog>
  );
};

export default AllReviewsDialog;
